import React, { useMemo, useRef, useState } from 'react';

import { Box, Button, Grid, Group, Paper, Stepper, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import GLMFlowBuilderMainContainer, {
  BuilderCard,
} from 'components/GLMFlowBuilder/GLMFlowBuilderMainContainer';
import KiloAlert from 'components/KiloAlert';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'context/auth-context';
import useCreateGLMKiloFlow from 'hooks/glmFlows/useCreateGLMKiloFlow';
import useGLMKiloFlow from 'hooks/glmFlows/useGLMKiloFlow';
import usePipelines, { GHLPipeline } from 'hooks/glmFlows/usePipelines';
import useUpdateGLMKiloFlow from 'hooks/glmFlows/useUpdateGLMKiloFlow';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate, useParams } from 'react-router-dom';
import { GLMKiloFlowCreate, GLMKiloFlowUpdate } from 'types';
import { getUpdatedFieldKeys, SelectorMap } from 'utils/compare-obj-functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import GLMKiloActionConfig from './components/GLMKiloActionConfig';
import GLMKiloReview from './components/GLMKiloReview';
import GLMKiloTriggerConfig from './components/GLMKiloTriggerConfig';
import GLMKiloTriggerSelect from './components/GLMKiloTriggerSelect';
import {
  ACTION_BY_TRIGGER,
  ACTION_LABELS,
  GLMKiloFlowTriggers,
  GLMKiloFormValues,
  LIFECYCLE_TRIGGERS,
  TRIGGER_LABELS,
} from './types';

type StepKey = 'trigger' | 'trigger_config' | 'action_config' | 'review';

const TRIGGERS_WITH_CONFIG = new Set<GLMKiloFlowTriggers>([
  GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT,
  GLMKiloFlowTriggers.TAG_ADDED,
]);

const getVisibleSteps = (trigger: GLMKiloFormValues['trigger']): StepKey[] => {
  const steps: StepKey[] = ['trigger'];
  if (trigger && TRIGGERS_WITH_CONFIG.has(trigger)) steps.push('trigger_config');
  if (trigger && LIFECYCLE_TRIGGERS.has(trigger)) steps.push('action_config');
  steps.push('review');
  return steps;
};

const STEP_LABELS: Record<StepKey, string> = {
  trigger: 'Trigger',
  trigger_config: 'Configure trigger',
  action_config: 'Set status',
  review: 'Review',
};

// Field selectors for computing changed_fields on update (Mixpanel).
const CHANGED_FIELD_SELECTORS: SelectorMap = {
  enabled: (v: GLMKiloFormValues) => v.enabled,
  tc_pipeline_id: (v: GLMKiloFormValues) => v.tcPipelineId,
  tc_stage_id: (v: GLMKiloFormValues) => v.tcStageId,
  tc_tag: (v: GLMKiloFormValues) => v.tcTag,
  ac_athlete_status: (v: GLMKiloFormValues) => v.acAthleteStatus,
};

// Build the ordered cards (GLM event → trigger config → Kilo action → status)
// shown in the shared GLMFlowBuilderMainContainer as the flow is built.
const buildGlmKiloCards = (
  v: GLMKiloFormValues,
  currentStep: StepKey,
  pipelines: GHLPipeline[],
): BuilderCard[] => {
  const cards: BuilderCard[] = [];
  if (!v.trigger) return cards;

  cards.push({
    key: 'trigger',
    cardType: 'trigger',
    headerLabel: 'GLM Event',
    value: TRIGGER_LABELS[v.trigger],
    placeholderText: 'select trigger',
    isSelected: currentStep === 'trigger',
  });

  if (v.trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT) {
    const pipeline = pipelines.find(p => p.id === v.tcPipelineId);
    const stage = pipeline?.stages.find(s => s.id === v.tcStageId);
    cards.push({
      key: 'config',
      cardType: 'pipeline_stage',
      headerLabel: 'Pipeline & Stage',
      value: pipeline && stage ? `${pipeline.name} → ${stage.name}` : null,
      placeholderText: 'select pipeline & stage',
      isSelected: currentStep === 'trigger_config',
    });
  } else if (v.trigger === GLMKiloFlowTriggers.TAG_ADDED) {
    cards.push({
      key: 'config',
      cardType: 'tag',
      headerLabel: 'Tag',
      value: v.tcTag,
      placeholderText: 'select tag',
      isSelected: currentStep === 'trigger_config',
    });
  }

  cards.push({
    key: 'action',
    cardType: 'action',
    headerLabel: 'Kilo Action',
    value: ACTION_LABELS[ACTION_BY_TRIGGER[v.trigger]],
    isSelected: false,
  });

  if (LIFECYCLE_TRIGGERS.has(v.trigger)) {
    cards.push({
      key: 'status',
      cardType: 'status',
      headerLabel: 'Athlete Status',
      value: v.acAthleteStatus,
      placeholderText: 'select status',
      isSelected: currentStep === 'action_config',
    });
  }

  return cards;
};

const GLMKiloFlowBuilder = (): JSX.Element => {
  const { session } = useAuth();
  const isDemoGym = !!session?.demoGym;
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();
  const isEdit = !!flowId;

  const createMutation = useCreateGLMKiloFlow();
  const updateMutation = useUpdateGLMKiloFlow();
  const { data: flowData, isSuccess } = useGLMKiloFlow(flowId);
  const { trackGLMFlowCreated, trackGLMFlowUpdated } = useStaffTracking();
  const initialValuesRef = useRef<GLMKiloFormValues | null>(null);

  const [active, setActive] = useState(0);

  const form = useForm<GLMKiloFormValues>({
    initialValues: {
      glmKiloFlowId: '',
      trigger: '',
      tcPipelineId: null,
      tcStageId: null,
      tcTag: null,
      acAthleteStatus: null,
      enabled: false,
    },
  });

  useShallowEffect(() => {
    if (isEdit && isSuccess && flowData) {
      const loadedValues: GLMKiloFormValues = {
        glmKiloFlowId: flowData.data.glmKiloFlowId,
        trigger: flowData.data.trigger as GLMKiloFlowTriggers,
        tcPipelineId: flowData.data.tcPipelineId,
        tcStageId: flowData.data.tcStageId,
        tcTag: flowData.data.tcTag,
        acAthleteStatus: flowData.data.acAthleteStatus,
        enabled: flowData.data.enabled,
      };
      form.setValues(loadedValues);
      initialValuesRef.current = loadedValues;
    }
  }, [isEdit, isSuccess, flowData]);

  const visibleSteps = useMemo(() => getVisibleSteps(form.values.trigger), [form.values.trigger]);
  const currentStep = visibleSteps[Math.min(active, visibleSteps.length - 1)];

  const { data: pipelines = [] } = usePipelines(
    form.values.trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT,
  );
  const cards = buildGlmKiloCards(form.values, currentStep, pipelines);

  const handleSelectTrigger = (trigger: GLMKiloFlowTriggers): void => {
    // Selecting a trigger resets downstream config so a re-pick can't leave stale values.
    form.setValues(prev => ({
      ...prev,
      trigger,
      tcPipelineId: null,
      tcStageId: null,
      tcTag: null,
      acAthleteStatus: null,
    }));
  };

  const isStepValid = (step: StepKey): boolean => {
    switch (step) {
      case 'trigger':
        return !!form.values.trigger;
      case 'trigger_config':
        if (form.values.trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT) {
          return !!form.values.tcPipelineId && !!form.values.tcStageId;
        }
        return !!form.values.tcTag; // TAG_ADDED
      case 'action_config':
        return !!form.values.acAthleteStatus;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleSave = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const { trigger } = form.values;
    if (!trigger) return;
    const isLifecycle = LIFECYCLE_TRIGGERS.has(trigger);
    const isPipelineStage = trigger === GLMKiloFlowTriggers.PIPELINE_STAGE_MOVEMENT;

    if (isEdit) {
      const payload: GLMKiloFlowUpdate = {
        glmKiloFlowId: form.values.glmKiloFlowId,
        enabled: form.values.enabled,
        tcPipelineId: isPipelineStage ? form.values.tcPipelineId : null,
        tcStageId: isPipelineStage ? form.values.tcStageId : null,
        tcTag: trigger === GLMKiloFlowTriggers.TAG_ADDED ? form.values.tcTag : null,
        acAthleteStatus: isLifecycle ? form.values.acAthleteStatus : null,
      };
      const changedFields = initialValuesRef.current
        ? getUpdatedFieldKeys(initialValuesRef.current, form.values, CHANGED_FIELD_SELECTORS)
        : [];
      updateMutation.mutate(payload, {
        onSuccess: () => {
          trackGLMFlowUpdated({
            flow_id: payload.glmKiloFlowId,
            direction: 'glm_to_kilo',
            trigger,
            action: ACTION_BY_TRIGGER[trigger],
            changed_fields: changedFields,
          });
          navigate('/integrations/glm?tab=glm-to-kilo');
          successNotification('GLM Kilo Flow Updated', 'Flow has been updated successfully');
        },
        onError: error => errorNotification('GLM Kilo Flow Update Failed', error.response?.data.message),
      });
      return;
    }

    const payload: GLMKiloFlowCreate = {
      glmKiloFlowId: uuidv4(),
      trigger,
      action: ACTION_BY_TRIGGER[trigger],
      enabled: form.values.enabled,
      tcPipelineId: isPipelineStage ? form.values.tcPipelineId : null,
      tcStageId: isPipelineStage ? form.values.tcStageId : null,
      tcTag: trigger === GLMKiloFlowTriggers.TAG_ADDED ? form.values.tcTag : null,
      acAthleteStatus: isLifecycle ? form.values.acAthleteStatus : null,
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        trackGLMFlowCreated({
          flow_id: payload.glmKiloFlowId,
          direction: 'glm_to_kilo',
          trigger: payload.trigger,
          action: payload.action,
          enabled: payload.enabled,
        });
        navigate('/integrations/glm?tab=glm-to-kilo');
        successNotification('GLM Kilo Flow Created', 'Flow has been created successfully');
      },
      onError: error => errorNotification('GLM Kilo Flow Creation Failed', error.response?.data.message),
    });
  };

  const isLastStep = active >= visibleSteps.length - 1;
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Paper
      shadow='xl'
      radius='md'
      withBorder
      py='sm'
      px={0}
      style={{ minWidth: 'fit-content', height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <PageHeader>
        <Group position='apart' align='center' noWrap>
          <Title order={1} size='h1'>
            GLM To Kilo Flow Builder
          </Title>
          <Button variant='subtle' color='red' onClick={() => navigate('/integrations/glm?tab=glm-to-kilo')}>
            Cancel
          </Button>
        </Group>
      </PageHeader>

      {isDemoGym && (
        <Box px='md' pb='sm'>
          <KiloAlert variantStyle='warning'>
            This is a simulated Gym Lead Machine integration for demo purposes only. It is not connected to a
            real account, and any triggers you create here won&apos;t sync anywhere.
          </KiloAlert>
        </Box>
      )}
      <Grid style={{ flex: 1, overflow: 'hidden', margin: 0 }}>
        <Grid.Col span={8} style={{ height: '100%' }}>
          <GLMFlowBuilderMainContainer cards={cards} />
        </Grid.Col>
        <Grid.Col
          span={4}
          style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
        >
          <Stepper
            active={active}
            onStepClick={setActive}
            size='sm'
            breakpoint='sm'
            px='md'
            py='md'
            style={{ flex: 1 }}
          >
            {visibleSteps.map(step => (
              <Stepper.Step key={step} label={STEP_LABELS[step]} allowStepSelect={false}>
                {step === 'trigger' && (
                  <GLMKiloTriggerSelect form={form} onSelect={handleSelectTrigger} locked={isEdit} />
                )}
                {step === 'trigger_config' && <GLMKiloTriggerConfig form={form} />}
                {step === 'action_config' && <GLMKiloActionConfig form={form} />}
                {step === 'review' && <GLMKiloReview form={form} />}
              </Stepper.Step>
            ))}
          </Stepper>

          <Group position='apart' px='md' pb='md'>
            <Button
              variant='default'
              disabled={active === 0}
              onClick={() => setActive(s => Math.max(0, s - 1))}
            >
              Back
            </Button>
            {isLastStep ? (
              <Button onClick={handleSave} loading={isSaving}>
                Save automation
              </Button>
            ) : (
              <Button
                disabled={!isStepValid(currentStep)}
                onClick={() => setActive(s => Math.min(visibleSteps.length - 1, s + 1))}
              >
                Continue
              </Button>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    </Paper>
  );
};

export default GLMKiloFlowBuilder;
